import { Refresh, SwapHoriz } from '@mui/icons-material';
import {
    Alert,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { useFetch } from '../../../../hooks/useFecth';
import useBeneficiaryMigrationSocket from '../../../../hooks/useBeneficiaryMigrationSocket';
import useErrors from '../../../../hooks/useErrors';
import server from '../../../../services/server';

const STATE_LABEL = {
    waiting: 'Na fila',
    delayed: 'Agendado',
    active: 'Processando',
    completed: 'Concluído',
    failed: 'Falhou',
    paused: 'Pausado',
};

function stateColor(state) {
    if (state === 'completed') return 'success';
    if (state === 'failed') return 'error';
    if (state === 'active') return 'primary';
    if (state === 'delayed') return 'warning';
    return 'default';
}

function formatWhen(value) {
    if (!value) return '—';
    return new Date(value).toLocaleString('pt-BR');
}

export default function ClubSubscriberMigration({ club }) {
    const clubId = club?.id;
    const { setErrors, enqueueSnackbar } = useErrors();
    const [enqueueing, setEnqueueing] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedPlanId, setSelectedPlanId] = useState('');
    const { data, mutate, isFetching } = useFetch(
        clubId ? `migration/subscriber/queue/${clubId}` : null,
    );
    const { data: plansData } = useFetch(clubId ? `clubs/${clubId}/plans` : null);
    const plans = Array.isArray(plansData) ? plansData : plansData?.rows || [];
    const counts = data?.counts || {};
    const jobs = data?.jobs || [];

    const onSocketUpdate = useCallback(() => {
        mutate();
    }, [mutate]);

    const { connected } = useBeneficiaryMigrationSocket(clubId, {
        onUpdate: onSocketUpdate,
        enabled: Boolean(club?.originId),
    });

    const grouped = useMemo(() => {
        const order = ['active', 'waiting', 'delayed', 'failed', 'completed'];
        return order
            .map(state => ({
                state,
                rows: jobs.filter(job => job.state === state),
            }))
            .filter(group => group.rows.length > 0);
    }, [jobs]);

    const handleOpenDialog = () => {
        setSelectedPlanId('');
        setDialogOpen(true);
    };

    const handleEnqueue = async () => {
        try {
            setEnqueueing(true);
            const { data: body } = await server.post('migration/subscriber', {
                clubId,
                planId: selectedPlanId || undefined,
            });
            enqueueSnackbar(body?.message || 'Assinantes enviados para a fila.');
            setDialogOpen(false);
            await mutate();
        } catch (error) {
            setErrors(error);
        } finally {
            setEnqueueing(false);
        }
    };

    if (!club?.originId) {
        return (
            <Alert severity="info">
                Este clube não veio do sistema legado. A migração de assinantes só vale para clubes
                com origem migrada.
            </Alert>
        );
    }

    return (
        <Stack spacing={2} pt={2}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={2}>
                <Stack spacing={0.5}>
                    <Typography variant="h6">Migração de assinantes</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Enfileira os beneficiários do legado. O progresso vem da fila (Redis), sem
                        tabela nova. Socket {connected ? 'conectado' : 'desconectado'}.
                    </Typography>
                </Stack>
                <Stack direction="row" gap={1}>
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Refresh />}
                        onClick={() => mutate()}
                        disabled={isFetching}
                    >
                        Atualizar
                    </Button>
                    <Button
                        size="small"
                        variant="contained"
                        startIcon={<SwapHoriz />}
                        onClick={handleOpenDialog}
                    >
                        Migrar assinantes
                    </Button>
                </Stack>
            </Stack>

            <Stack direction="row" gap={1} flexWrap="wrap">
                <Chip size="small" label={`Total ${counts.total || 0}`} />
                <Chip size="small" color="info" variant="outlined" label={`Fila ${counts.pending || 0}`} />
                <Chip
                    size="small"
                    color="success"
                    variant="outlined"
                    label={`Ok ${counts.completed || 0}`}
                />
                <Chip
                    size="small"
                    color="error"
                    variant="outlined"
                    label={`Erro ${counts.failed || 0}`}
                />
            </Stack>

            {jobs.length === 0 && !isFetching ? (
                <Alert severity="info">Nenhum job na fila deste clube ainda.</Alert>
            ) : (
                grouped.map(group => (
                    <Paper key={group.state} variant="outlined">
                        <Stack px={2} py={1.5} direction="row" alignItems="center" gap={1}>
                            <Chip
                                size="small"
                                color={stateColor(group.state)}
                                label={`${STATE_LABEL[group.state] || group.state} (${group.rows.length})`}
                            />
                        </Stack>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Nome</TableCell>
                                        <TableCell>E-mail</TableCell>
                                        <TableCell>Documento</TableCell>
                                        <TableCell>ID legado</TableCell>
                                        <TableCell>Quando</TableCell>
                                        <TableCell>Detalhe</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {group.rows.map(row => (
                                        <TableRow key={row.id} hover>
                                            <TableCell>{row.name || '—'}</TableCell>
                                            <TableCell>{row.email || '—'}</TableCell>
                                            <TableCell>{row.document || '—'}</TableCell>
                                            <TableCell>{row.originId ?? '—'}</TableCell>
                                            <TableCell>
                                                {formatWhen(row.finishedOn || row.processedOn || row.timestamp)}
                                            </TableCell>
                                            <TableCell>
                                                {row.failedReason ||
                                                    (row.state === 'completed' ? 'Migrado' : '—')}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                ))
            )}

            <Dialog open={dialogOpen} onClose={() => !enqueueing && setDialogOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Migrar assinantes</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} pt={1}>
                        <Typography variant="body2" color="text.secondary">
                            Se escolher um plano, a migração cria a assinatura com esse plano. Quem já
                            foi migrado sem plano pode ser enfileirado de novo para receber o plano.
                        </Typography>
                        <FormControl fullWidth size="small">
                            <InputLabel id="migration-plan-label">Plano</InputLabel>
                            <Select
                                labelId="migration-plan-label"
                                label="Plano"
                                value={selectedPlanId}
                                onChange={e => setSelectedPlanId(e.target.value)}
                            >
                                <MenuItem value="">
                                    <em>Sem plano</em>
                                </MenuItem>
                                {plans
                                    .filter(plan => plan?.id && !plan.deletedAt)
                                    .map(plan => (
                                        <MenuItem key={plan.id} value={String(plan.id)}>
                                            {plan.name || `Plano #${plan.id}`}
                                            {plan.price != null
                                                ? ` · ${Number(plan.price).toLocaleString('pt-BR', {
                                                      style: 'currency',
                                                      currency: 'BRL',
                                                  })}`
                                                : ''}
                                        </MenuItem>
                                    ))}
                            </Select>
                        </FormControl>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)} disabled={enqueueing}>
                        Cancelar
                    </Button>
                    <Button variant="contained" onClick={handleEnqueue} disabled={enqueueing}>
                        {enqueueing ? 'Enfileirando…' : 'Enfileirar'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
}
